import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, Clock, User, Share2, Linkedin, Twitter, Facebook } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useBlogContactPopup } from "@/hooks/useBlogContactPopup";
import DemoScheduler from "@/components/DemoScheduler";
import SEO from "@/components/SEO";

const BlogPostAutomacoes = () => {
    const { isOpen, setIsOpen } = useBlogContactPopup(3000);

    return (
        <div className="min-h-screen bg-background">
            <SEO
                title="10 Automações Que Todo Service Desk Deveria Ter"
                description="Descubra as 10 automações essenciais para transformar seu Service Desk em 2025: triagem inteligente, chatbots com IA, workflows automáticos, gestão de SLA e muito mais. Reduza 22% do volume de tickets com automação."
                canonical="https://aroan.com.br/blog/10-automacoes-service-desk"
                ogImage="https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&auto=format&fit=crop"
                author="Carlos Mendes"
                publishedTime="2024-10-27T00:00:00-03:00"
                keywords="automação de service desk, ITSM, chatbot, workflows, triagem inteligente, onboarding, gestão de SLA, base de conhecimento"
            />
            <Header />

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 bg-gradient-hero overflow-hidden">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <Badge className="mb-6 bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                            Automação
                        </Badge>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-8 leading-tight animate-fade-in">
                            10 Automações Que Todo <span className="text-primary">Service Desk</span> Deveria Ter
                        </h1>

                        <div className="flex flex-wrap items-center justify-center gap-6 text-muted-foreground animate-fade-in-delay-1">
                            <div className="flex items-center gap-2">
                                <User className="w-5 h-5 text-primary" />
                                <span className="font-medium text-foreground">Carlos Mendes</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-primary" />
                                <span>27 Out, 2024</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Clock className="w-5 h-5 text-primary" />
                                <span>9 min de leitura</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Image */}
            <section className="pb-12">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-2xl animate-scale-in">
                        <img
                            src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&auto=format&fit=crop"
                            alt="Automações para Service Desk"
                            className="w-full h-[400px] sm:h-[500px] object-cover"
                        />
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="py-12 bg-background">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Two Column Layout */}
                    <div className="max-w-7xl mx-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

                            {/* Left Column */}
                            <div className="space-y-8">
                                {/* Executive Summary */}
                                <div className="prose prose-lg dark:prose-invert max-w-none">
                                    <h2 className="text-3xl font-bold mb-6 text-foreground">Sumário Executivo</h2>
                                    <p className="text-lg leading-relaxed">
                                        Em 2025, um Service Desk eficiente não é mais medido pela rapidez com que seus agentes atendem ao telefone, mas pela inteligência com que a tecnologia elimina a necessidade da chamada em primeiro lugar.
                                    </p>
                                    <p>
                                        A automação deixou de ser um diferencial para se tornar a espinha dorsal de qualquer operação de ITSM (IT Service Management) de alta performance. Tarefas repetitivas, como redefinição de senhas, que chegam a consumir <strong>22% do volume total de tickets</strong> em algumas organizações, são o principal alvo da automação.
                                    </p>
                                    <p>
                                        Ao automatizar processos-chave, as empresas liberam suas equipes para focar em iniciativas estratégicas, reduzem custos operacionais e melhoram drasticamente a experiência do colaborador. Este relatório detalha as 10 automações essenciais que todo Service Desk moderno deveria implementar para se manter competitivo e agregar valor real ao negócio.
                                    </p>
                                </div>

                                {/* Automation 1 */}
                                <div className="bg-card p-6 rounded-xl border border-border">
                                    <h3 className="text-xl font-bold mb-4 text-primary">1. Triagem, Categorização e Roteamento Inteligente de Tickets</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-2">O Problema:</h4>
                                            <p className="text-muted-foreground">
                                                Tickets chegam sem contexto, são atribuídos manualmente ao agente errado e perdem-se em filas, aumentando o tempo de resolução.
                                            </p>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-2">A Automação:</h4>
                                            <p className="text-muted-foreground">
                                                Utilizando IA, o sistema analisa o conteúdo de um novo ticket (via e-mail, portal ou chat), o categoriza (ex: "Hardware", "Software", "Rede"), define sua prioridade com base em palavras-chave ou no perfil do solicitante, e o atribui automaticamente ao grupo ou agente mais qualificado. Isso elimina a triagem manual e garante que o especialista certo comece a trabalhar no problema imediatamente.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Automation 2 */}
                                <div className="bg-card p-6 rounded-xl border border-border">
                                    <h3 className="text-xl font-bold mb-4 text-primary">2. Resolução de Nível 1 com Chatbots e Base de Conhecimento</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-2">O Problema:</h4>
                                            <p className="text-muted-foreground">
                                                Agentes de Nível 1 gastam a maior parte do tempo respondendo a perguntas repetitivas e resolvendo problemas simples, como redefinição de senhas ou configuração de Wi-Fi.
                                            </p>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-2">A Automação:</h4>
                                            <p className="text-muted-foreground">
                                                Um chatbot com IA, integrado à base de conhecimento da empresa, atua como o primeiro ponto de contato. Ele entende a linguagem natural do usuário e oferece soluções passo a passo, artigos ou tutoriais. Para solicitações comuns, como redefinir uma senha, o chatbot pode executar o workflow de automação diretamente, resolvendo o problema em segundos, 24/7, sem qualquer intervenção humana.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Automation 3 */}
                                <div className="bg-card p-6 rounded-xl border border-border">
                                    <h3 className="text-xl font-bold mb-4 text-primary">3. Automação de Onboarding e Offboarding de Colaboradores</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-2">O Problema:</h4>
                                            <p className="text-muted-foreground">
                                                O processo de entrada e saída de funcionários é complexo, propenso a erros e envolve múltiplos departamentos (TI, RH, Finanças), resultando em atrasos e falhas de segurança.
                                            </p>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-2">A Automação:</h4>
                                            <p className="text-muted-foreground">
                                                Um único formulário de solicitação no portal de serviços dispara um workflow completo. Para o onboarding, isso pode incluir a criação automática de contas (AD, E-mail, etc.), concessão de acesso a sistemas, provisionamento de hardware e agendamento de treinamentos. No offboarding, o processo inverso é executado, garantindo que todos os acessos sejam revogados de forma segura e imediata.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Automation 4 */}
                                <div className="bg-card p-6 rounded-xl border border-border">
                                    <h3 className="text-xl font-bold mb-4 text-primary">4. Gestão de SLA e Escalação Automática</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-2">O Problema:</h4>
                                            <p className="text-muted-foreground">
                                                O controle manual de SLAs (Service Level Agreements) é ineficiente. Tickets importantes são esquecidos e violam os prazos, causando insatisfação e impacto nos negócios.
                                            </p>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-2">A Automação:</h4>
                                            <p className="text-muted-foreground">
                                                O sistema monitora automaticamente o tempo de vida de cada ticket. Regras pré-definidas disparam alertas para os agentes quando um SLA está prestes a ser violado. Se o prazo expirar, o ticket é automaticamente escalado para o próximo nível de suporte ou para um gerente, garantindo que problemas críticos recebam a atenção necessária.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Automation 5 */}
                                <div className="bg-card p-6 rounded-xl border border-border">
                                    <h3 className="text-xl font-bold mb-4 text-primary">5. Automação de Fluxos de Aprovação</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-2">O Problema:</h4>
                                            <p className="text-muted-foreground">
                                                Solicitações de novos softwares, equipamentos ou acesso a dados sensíveis ficam paradas por dias, aguardando aprovações manuais via e-mail.
                                            </p>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-2">A Automação:</h4>
                                            <p className="text-muted-foreground">
                                                Workflows de aprovação de múltiplos estágios são configurados no sistema. Quando uma solicitação é feita, os aprovadores designados recebem uma notificação (via e-mail, Slack ou Teams) com um link para aprovar ou rejeitar com um único clique. Uma vez aprovado, o workflow prossegue automaticamente para a próxima etapa (ex: instalação do software).
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column */}
                            <div className="space-y-8">
                                {/* Automation 6 */}
                                <div className="bg-card p-6 rounded-xl border border-border">
                                    <h3 className="text-xl font-bold mb-4 text-primary">6. Monitoramento Proativo e Criação de Incidentes</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-2">O Problema:</h4>
                                            <p className="text-muted-foreground">
                                                O Service Desk opera de forma reativa, esperando que os usuários relatem problemas que já estão impactando a produtividade.
                                            </p>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-2">A Automação:</h4>
                                            <p className="text-muted-foreground">
                                                A plataforma de ITSM é integrada a ferramentas de monitoramento de infraestrutura. Quando um sistema crítico (como um servidor ou um serviço de rede) apresenta sinais de falha, um ticket de incidente de alta prioridade é criado e atribuído automaticamente, permitindo que a equipe de TI resolva o problema antes que os usuários sejam afetados.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Automation 7 */}
                                <div className="bg-card p-6 rounded-xl border border-border">
                                    <h3 className="text-xl font-bold mb-4 text-primary">7. Automação da Coleta de Feedback de Satisfação</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-2">O Problema:</h4>
                                            <p className="text-muted-foreground">
                                                A coleta manual de feedback é esporádica e inconsistente, impedindo a medição real da qualidade do serviço.
                                            </p>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-2">A Automação:</h4>
                                            <p className="text-muted-foreground">
                                                Assim que um ticket é fechado, o sistema envia automaticamente uma pesquisa de satisfação (CSAT) para o usuário. Os resultados são compilados em dashboards em tempo real, fornecendo aos gestores uma visão clara da performance da equipe e identificando áreas para melhoria.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Automation 8 */}
                                <div className="bg-card p-6 rounded-xl border border-border">
                                    <h3 className="text-xl font-bold mb-4 text-primary">8. Sugestão e Criação de Artigos para a Base de Conhecimento</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-2">O Problema:</h4>
                                            <p className="text-muted-foreground">
                                                A base de conhecimento fica desatualizada rapidamente porque a criação de novos artigos é um processo manual e demorado.
                                            </p>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-2">A Automação:</h4>
                                            <p className="text-muted-foreground">
                                                A IA analisa os tickets resolvidos e identifica soluções que não estão documentadas na base de conhecimento. O sistema pode então sugerir a criação de um novo artigo para o agente ou até mesmo gerar um rascunho do artigo automaticamente, usando os detalhes da resolução do ticket.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Automation 9 */}
                                <div className="bg-card p-6 rounded-xl border border-border">
                                    <h3 className="text-xl font-bold mb-4 text-primary">9. Relatórios e Dashboards Automatizados</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-2">O Problema:</h4>
                                            <p className="text-muted-foreground">
                                                A criação de relatórios de performance é um trabalho manual e tedioso, que consome tempo valioso dos gestores.
                                            </p>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-2">A Automação:</h4>
                                            <p className="text-muted-foreground">
                                                Dashboards em tempo real exibem métricas-chave (KPIs) como volume de tickets, tempo médio de resolução, taxa de resolução no primeiro contato e conformidade com SLA. Relatórios periódicos são gerados e distribuídos automaticamente para as partes interessadas, garantindo total visibilidade sobre a saúde da operação.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Automation 10 */}
                                <div className="bg-card p-6 rounded-xl border border-border">
                                    <h3 className="text-xl font-bold mb-4 text-primary">10. Integração com Ferramentas de Colaboração (Slack/Teams)</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-2">O Problema:</h4>
                                            <p className="text-muted-foreground">
                                                Os funcionários precisam sair de seu ambiente de trabalho principal para abrir um ticket, criando uma barreira e incentivando o "shadow IT".
                                            </p>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-2">A Automação:</h4>
                                            <p className="text-muted-foreground">
                                                O Service Desk é integrado diretamente em plataformas como Slack ou Microsoft Teams. Os usuários podem interagir com o chatbot de IA, abrir tickets, verificar o status de suas solicitações e receber notificações, tudo dentro da ferramenta de comunicação que já utilizam no dia a dia, tornando o processo de suporte fluido e intuitivo.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* References */}
                                <div className="bg-muted/30 p-6 rounded-xl border border-border">
                                    <h3 className="text-lg font-bold mb-4 text-foreground">Referências</h3>
                                    <div className="text-sm text-muted-foreground space-y-2">
                                        <p>
                                            Freshworks. (2025, 25 de Junho). <em>11 ITSM Best Practices Every IT Team Should Follow in 2025</em>.
                                            <a href="https://www.freshworks.com/itsm/best-practices/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">
                                                Recuperado aqui
                                            </a>
                                        </p>
                                        <p>
                                            Wrangle. (2025, 8 de Janeiro). <em>12 Service Desk Automation Ideas + How to Implement Them</em>.
                                            <a href="https://www.wrangle.io/post/12-service-desk-automation-ideas-how-to-implement-them" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">
                                                Recuperado aqui
                                            </a>
                                        </p>
                                        <p>
                                            Rezolve.ai. (2025, 10 de Janeiro). <em>Top 10 tasks to Automate in IT Helpdesk</em>.
                                            <a href="https://www.rezolve.ai/blog/top-10-tasks-to-automate-in-it-helpdesk" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">
                                                Recuperado aqui
                                            </a>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Share Section */}
                        <div className="flex items-center justify-between border-y border-border py-6 mb-12 mt-12">
                            <span className="font-semibold">Compartilhe este artigo:</span>
                            <div className="flex gap-4">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="hover:text-blue-600"
                                    onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank')}
                                >
                                    <Linkedin className="w-5 h-5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="hover:text-blue-400"
                                    onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent("Confira este artigo sobre 10 Automações Essenciais para Service Desk!")}`, '_blank')}
                                >
                                    <Twitter className="w-5 h-5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="hover:text-blue-700"
                                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')}
                                >
                                    <Facebook className="w-5 h-5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={async () => {
                                        if (navigator.share) {
                                            try {
                                                await navigator.share({
                                                    title: '10 Automações Que Todo Service Desk Deveria Ter',
                                                    text: 'Confira este artigo sobre automações essenciais para Service Desk!',
                                                    url: window.location.href,
                                                });
                                            } catch (err) {
                                                console.error('Error sharing:', err);
                                            }
                                        } else {
                                            navigator.clipboard.writeText(window.location.href);
                                            alert("Link copiado para a área de transferência!");
                                        }
                                    }}
                                >
                                    <Share2 className="w-5 h-5" />
                                </Button>
                            </div>
                        </div>

                        {/* CTA */}
                        <div className="bg-foreground text-background rounded-2xl p-8 sm:p-12 text-center">
                            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                                Pronto para implementar essas automações no seu Service Desk?
                            </h2>
                            <p className="text-background/80 mb-8 max-w-2xl mx-auto">
                                Descubra como a AROAN pode ajudar você a automatizar todos esses processos em uma única plataforma integrada.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button size="lg" variant="secondary" className="group" onClick={() => window.open('/#demo', '_self')}>
                                    Agende uma Demonstração
                                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                                </Button>
                                <Button size="lg" variant="outline" className="bg-transparent border-background/20 text-background hover:bg-background/10" onClick={() => window.open('/materiais', '_self')}>
                                    Baixe o Guia Gratuito
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />

            <DemoScheduler open={isOpen} onOpenChange={setIsOpen} />
        </div>
    );
};

export default BlogPostAutomacoes;
